// React
import { create } from 'zustand';

// Style
import { green } from '@mui/material/colors/index.js';

// Other
import { formatCalendarWeekString, getWeek } from '../utils/index.js';

const useSiteStore = create((set, get) => ({
	mobile: null,
	setMobile: (mobile) => set(({ mobile: mobile })),

	year: new Date().getFullYear(),
	setYear: (year) => set(({ year: year })),

	month: new Date().getMonth(),
	setMonth: (month) => set(({ month: month })),
	isLoading: true,
	setLoading: (isLoading) => set(({ isLoading: isLoading })),

	selectedTab: JSON.parse(localStorage.getItem('selectedTab')) || 0,
	setSelectedTab: (selectedTab) => set(({ selectedTab: selectedTab })),

	weeklySchedule: undefined,
	setWeeklySchedule: (weeklySchedule) => set(({ weeklySchedule: weeklySchedule })),
	removeTrainingSession: (sessionId) => set((state) => {
        // 1. Prüfen ob überhaupt ein Plan da ist
        if (!state.weeklySchedule || !state.weeklySchedule.trainingSessions) return state;

        // 2. Neue Liste ohne die gelöschte Session erstellen
        const updatedSessions = state.weeklySchedule.trainingSessions.filter(
            session => session.trainingsessionid !== sessionId
        );

        // 3. Den State mit der neuen Liste aktualisieren
        return {
            weeklySchedule: {
                ...state.weeklySchedule,
                trainingSessions: updatedSessions
            }
        };
    }),

	updateTrainingSession: (updatedSession) => {
    const weeklySchedule = get().weeklySchedule;
		if (!weeklySchedule || !weeklySchedule.trainingSessions) return;
		// Neue Liste erstellen: Die geänderte Session austauschen, alle anderen behalten
		const updatedSessions = weeklySchedule.trainingSessions.map(session => 
			session.trainingsessionid === updatedSession.trainingsessionid 
				? { ...session, ...updatedSession } 
				: session                           
		);

		set({
			weeklySchedule: {
				...weeklySchedule,
				trainingSessions: updatedSessions
			}
		});
	},
	removeWeeklySchedule: (calendarWeek, year) => {
		const weeklySchedule = get().weeklySchedule;
		
		// Nur löschen, wenn der aktuell angezeigte Plan (im State) 
		// auch wirklich der ist, den wir gerade gelöscht haben (KW & Jahr stimmen überein)
		if (weeklySchedule && weeklySchedule.calendarWeek === calendarWeek && get().year === year) {
			set({ weeklySchedule: undefined });
		}
	},

	openWeeklySchedule: JSON.parse(localStorage.getItem('openWeeklySchedule_mobile')) || false,
	setOpenWeeklySchedule: (openWeeklySchedule) => set(({ openWeeklySchedule: openWeeklySchedule })),

	selectedSortingCriteria: JSON.parse(localStorage.getItem('selectedSortingCriteria')) || "AmpelAuf",
	setSelectedSortingCriteria: (selectedSortingCriteria) => set(({ selectedSortingCriteria: selectedSortingCriteria })),

	selectedAthlete: undefined,
    setSelectedAthlete: (athlete) => set(({ selectedAthlete: athlete })),

	feedbacks: [],
	setFeedbacks: (feedbacks) => set(({ feedbacks: feedbacks })),

	isWeeklyScheduleLoading: false,
	setWeeklyScheduleLoading: (isWeeklyScheduleLoading) => set(({ isWeeklyScheduleLoading: isWeeklyScheduleLoading })),

	selectedCalenderWeek: JSON.parse(localStorage.getItem('selectedCalenderWeek')) || getWeek(new Date()),
	setSelectedCalenderWeek: (selectedCalenderWeek) => set(({ selectedCalenderWeek: selectedCalenderWeek })),

	snackbarOpenSuccess: false,
	setSnackbarOpenSuccess: (snackbarOpenSuccess) => set({ snackbarOpenSuccess: snackbarOpenSuccess }),

	selectedViewAthletesTab: JSON.parse(localStorage.getItem('selectedViewAthletesTab')) || "calendarYear",
	setSelectedViewAthletesTab: (selectedViewAthletesTab) => set(({ selectedViewAthletesTab: selectedViewAthletesTab })),

	snackbarOpenFail: false,
	setSnackbarOpenFail: (snackbarOpenFail) => set(({ snackbarOpenFail: snackbarOpenFail })),

	calendarWeekString: () => formatCalendarWeekString(get().selectedCalenderWeek, get().year),
	setCalendarWeekString: (calendarWeekString) => set(({ calendarWeekString: calendarWeekString })),

	allOpen: false,
	setAllOpen: (allOpen) => set(({ allOpen: allOpen })),

	tableRowsOpen: [],
	setTableRowsOpen: (tableRowsOpen) =>
		set((state) => ({
			tableRowsOpen:
				typeof tableRowsOpen === 'function'
					? tableRowsOpen(state.tableRowsOpen)
					: tableRowsOpen,
		})),

	openFeedback: false,
	setOpenFeedback: (openFeedback) => set(({ openFeedback: openFeedback })),

	openEditTrainingSession: false,
	setOpenEditTrainingSession: (openEditTrainingSession) => set(({ openEditTrainingSession: openEditTrainingSession })),

	pendingOpenTrainingSessionId: null,
	setPendingOpenTrainingSessionId: (pendingOpenTrainingSessionId) => set(({ pendingOpenTrainingSessionId })),

	failMessage: "",
	setFailMessage: (failMessage) => set(({ failMessage: failMessage })), 
	
	successMessage: "",
	setSuccessMessage: (successMessage) => set(({ successMessage: successMessage })),

	darkMode: JSON.parse(localStorage.getItem('darkMode')) || false,
	setDarkMode: (darkMode) => {
		localStorage.setItem('darkMode', JSON.stringify(darkMode));
		set({ darkMode: darkMode });
	},

	highlightCurrentCalenderWeek: (calendarWeek, direction) => {
		const currentWeek = getWeek(new Date());
		const currentYear = new Date().getFullYear();
		const selectedYear = get().year;
		
		// Compare year first, then week
		let weekMatch = {};
		
		if (selectedYear > currentYear) {
			// Future year - "less" is true (previous arrow green, going back toward current)
			weekMatch = { equal: false, less: true, more: false };
		} else if (selectedYear < currentYear) {
			// Past year - "more" is true (next arrow green, going forward toward current)
			weekMatch = { equal: false, less: false, more: true };
		} else {
			// Same year - compare weeks
			weekMatch = {
				equal: currentWeek === calendarWeek,
				less: currentWeek < calendarWeek,
				more: currentWeek > calendarWeek
			};
		}
	
		return weekMatch[direction] ? green[600] : 'black';
	},

	renderCalenderWeek: (forward) => {
		const nextWeek = get().selectedCalenderWeek + (forward ? 1 : -1);
		return nextWeek > 52 ? '1' : nextWeek < 1 ? '52' : `${nextWeek}`;
	},

	changeCalenderWeek: (forward) => {
		if (forward && (get().selectedCalenderWeek + 1) > 52) {
			set(({ selectedCalenderWeek: 1 }));
			set(({ year: get().year + 1 }));
			return;
		}

		if (forward) {
			set(({ selectedCalenderWeek: get().selectedCalenderWeek + 1 }))
			return;
		}

		if ((get().selectedCalenderWeek - 1) < 1) {
			set(({ selectedCalenderWeek: 52 }))
			set(({ year: get().year - 1 }));
			return;
		}

		set(({ selectedCalenderWeek: get().selectedCalenderWeek - 1 }))
	}
}));

export default useSiteStore;
